import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageShell from '../components/ui/PageShell';
import ResponsiveTable from '../components/ui/ResponsiveTable';
import Modal from '../components/ui/Modal';
import Button from '../components/ui/Button';
import { Globals } from '../globalfunctions.js';
import { apiClient } from '../utils/apiClient';
import './Uptime.css';

// The page refreshes itself on the same cadence the server checks on, so a
// dashboard left open on a wall doesn't quietly go stale.
const REFRESH_MS = 60000;

const formatWhen = (value) => {
  if (!value) return 'Never';
  const date = new Date(value);
  const minutes = Math.round((Date.now() - date.getTime()) / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} min ago`;
  if (minutes < 60 * 24) return `${Math.round(minutes / 60)} h ago`;
  return date.toLocaleString();
};

const formatUptime = (value) => (value === null || value === undefined ? '—' : `${value}%`);

const uptimeClass = (value) => {
  if (value === null || value === undefined) return '';
  if (value >= 99.5) return 'uptime-good';
  if (value >= 95) return 'uptime-warn';
  return 'uptime-bad';
};

const formatDuration = (from, to) => {
  const minutes = Math.round(((to ? new Date(to) : new Date()).getTime() - new Date(from).getTime()) / 60000);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
};

function Uptime() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    if (Globals.LoginTime === null) {
      localStorage.setItem('redirectAfterLogin', window.location.pathname);
      navigate('/login');
    }
  }, [navigate]);

  const load = useCallback(async () => {
    try {
      setData(await apiClient.get('/api/uptime'));
      setError('');
    } catch (err) {
      console.error('Error loading uptime:', err);
      setError(err.message || 'Could not load uptime.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  const recheck = async (hostname) => {
    setChecking(hostname || 'all');
    try {
      await apiClient.post('/api/uptime/check', hostname ? { hostname } : {});
      await load();
    } catch (err) {
      setError(err.message || 'Could not run that check.');
    } finally {
      setChecking(null);
    }
  };

  const openDetail = async (site) => {
    setDetail({ hostname: site.hostname, checks: [], outages: [] });
    setDetailLoading(true);
    try {
      setDetail(await apiClient.get(`/api/uptime/${encodeURIComponent(site.hostname)}`));
    } catch (err) {
      setError(err.message || 'Could not load that history.');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const columns = [
    {
      key: 'hostname',
      label: 'Site',
      sortable: true,
      render: (site) => (
        <div className="uptime-site">
          <span className={`uptime-dot uptime-dot-${site.state}`} aria-hidden="true"></span>
          <div>
            <div className="uptime-host">{site.hostname}</div>
            {site.state === 'down' && site.last_error && (
              <div className="uptime-reason">
                {site.last_error} · {site.consecutive_failures} failed check{site.consecutive_failures === 1 ? '' : 's'} in a row
              </div>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'state',
      label: 'Status',
      sortable: true,
      render: (site) => <span className={`uptime-badge uptime-badge-${site.state}`}>{site.state}</span>
    },
    {
      key: 'last_response_ms',
      label: 'Response',
      sortable: true,
      sortValue: (site) => Number(site.last_response_ms) || 0,
      render: (site) => (site.state === 'up' && site.last_response_ms ? `${site.last_response_ms} ms` : '—')
    },
    {
      key: 'uptime_24h',
      label: '24h',
      sortable: true,
      sortValue: (site) => (site.uptime_24h === null ? -1 : site.uptime_24h),
      render: (site) => <span className={uptimeClass(site.uptime_24h)}>{formatUptime(site.uptime_24h)}</span>
    },
    {
      key: 'uptime_7d',
      label: '7d',
      sortable: true,
      sortValue: (site) => (site.uptime_7d === null ? -1 : site.uptime_7d),
      render: (site) => <span className={uptimeClass(site.uptime_7d)}>{formatUptime(site.uptime_7d)}</span>
    },
    {
      key: 'uptime_30d',
      label: '30d',
      sortable: true,
      sortValue: (site) => (site.uptime_30d === null ? -1 : site.uptime_30d),
      render: (site) => <span className={uptimeClass(site.uptime_30d)}>{formatUptime(site.uptime_30d)}</span>
    },
    {
      key: 'last_checked_at',
      label: 'Checked',
      sortable: true,
      render: (site) => formatWhen(site.last_checked_at)
    },
    {
      key: 'actions',
      label: '',
      render: (site) => (
        <div className="uptime-actions">
          <Button variant="secondary" onClick={(e) => { e.stopPropagation(); openDetail(site); }}>History</Button>
          <Button
            onClick={(e) => { e.stopPropagation(); recheck(site.hostname); }}
            disabled={checking === site.hostname}
          >
            {checking === site.hostname ? 'Checking...' : 'Re-check'}
          </Button>
        </div>
      )
    }
  ];

  const summary = data?.summary;

  return (
    <PageShell>
      <div className="uptime-header">
        <div>
          <h1>Outage Tracker</h1>
          <p className="uptime-intro">
            Every site we invoice for is checked every 5 minutes. The list comes from the remarks
            on invoice line items, so a site starts being watched as soon as it's billed.
            {data && ` Slack is notified after ${data.alert_after_failures} consecutive failures, but only for a site that has been seen working — a name that has never resolved is bad invoice data, not an outage.`}
          </p>
        </div>
        <Button onClick={() => recheck(null)} disabled={checking !== null}>
          {checking === 'all' ? 'Checking...' : 'Check all now'}
        </Button>
      </div>

      {error && <div className="uptime-error">{error}</div>}

      {summary && (
        <div className="uptime-summary">
          <div className="uptime-card uptime-card-up">
            <span className="uptime-card-value">{summary.up}</span>
            <span className="uptime-card-label">Up</span>
          </div>
          <div className={`uptime-card ${summary.down > 0 ? 'uptime-card-down' : ''}`}>
            <span className="uptime-card-value">{summary.down}</span>
            <span className="uptime-card-label">Down</span>
          </div>
          <div className="uptime-card">
            <span className="uptime-card-value">{summary.pending}</span>
            <span className="uptime-card-label">Not yet checked</span>
          </div>
          <div className="uptime-card">
            <span className="uptime-card-value">{summary.total}</span>
            <span className="uptime-card-label">Sites watched</span>
          </div>
        </div>
      )}

      {loading ? (
        <p>Loading uptime...</p>
      ) : (
        <ResponsiveTable
          columns={columns}
          rows={data?.sites || []}
          rowKey="hostname"
          emptyMessage="No sites to watch yet — a site appears here once a hostname is put in the remarks of an invoice line."
        />
      )}

      <Modal
        open={detail !== null}
        onClose={() => setDetail(null)}
        title={detail ? detail.hostname : ''}
        size="large"
      >
        {detailLoading ? (
          <p>Loading history...</p>
        ) : detail && (
          <div className="uptime-detail">
            <h4>Outages (last 30 days)</h4>
            {detail.outages.length === 0 ? (
              <p className="uptime-none">No failed checks recorded.</p>
            ) : (
              <ul className="uptime-outages">
                {detail.outages.map((outage, index) => (
                  <li key={index}>
                    <span className={`uptime-badge ${outage.ended_at ? 'uptime-badge-resolved' : 'uptime-badge-down'}`}>
                      {outage.ended_at ? 'Resolved' : 'Ongoing'}
                    </span>
                    <span className="uptime-outage-when">{new Date(outage.started_at).toLocaleString()}</span>
                    <span className="uptime-outage-len">{formatDuration(outage.started_at, outage.ended_at)}</span>
                    <span className="uptime-outage-err">{outage.error || 'No response'}</span>
                  </li>
                ))}
              </ul>
            )}

            <h4>Recent checks</h4>
            <div className="uptime-checks">
              {detail.checks.slice(0, 60).map((check) => (
                <span
                  key={check.id}
                  className={`uptime-tick ${check.ok ? 'is-up' : 'is-down'}`}
                  title={`${new Date(check.createdAt).toLocaleString()} — ${check.ok ? `HTTP ${check.status_code} in ${check.response_ms}ms` : (check.error || 'failed')}`}
                ></span>
              ))}
            </div>
          </div>
        )}
      </Modal>
    </PageShell>
  );
}

export default Uptime;
